import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { LoadingSpinner } from '../../../shared/ui';
import {
  fetchYowanAdminOrganizerList,
  fetchYowanAdminSeminarSeriesDetail,
  getYowanAdminPublicVisibilityStatusUi,
  getYowanPublicVisibilityDiagnostics,
  getYowanPublicVisibilityMessages,
  getYowanSeriesAdminWarningIssues,
  getYowanAdminMutationErrorMessage,
  getYowanApiFieldErrorMessage,
  updateYowanAdminSeminarSeries,
  validateYowanImageUploadFile,
  YOWAN_IMAGE_UPLOAD_HELP_TEXT,
} from '../app/yowanContentClient';
import {
  AdminBlogShell,
  AdminCurrentImagePreview,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
const { yowanPageContent } = require('./pageContent');

const ADMIN_SERIES_OPTION_PAGE_SIZE = 200;

const isInactiveMaster = (row = {}) => row.isActive === false || row.is_active === false;

const buildOrganizerOptions = (rows = []) => [
  { value: '', label: '主催者を選択' },
  ...rows.map((row) => {
    const isInactiveRetained = row.retained === true && isInactiveMaster(row);

    return {
      value: row.id || '',
      label: `${row.organizerName || row.organizer_name || row.representativeName || row.representative_name || row.id || '未設定'}${isInactiveRetained ? '（停止中・現在値）' : ''}`,
      disabled: isInactiveRetained,
    };
  }),
];

const mergeRowsById = (...groups) => {
  const rowsById = new Map();

  groups.flat().forEach((row) => {
    if (row?.id && !rowsById.has(row.id)) {
      rowsById.set(row.id, row);
    }
  });

  return Array.from(rowsById.values());
};

const markMissingReferencedRowsInactive = (referencedRows = [], activeRows = []) => {
  const activeIds = new Set(activeRows.map((row) => row?.id).filter(Boolean));

  return referencedRows.map((row) => {
    const isMissingFromActiveList = row?.id && !activeIds.has(row.id);

    return {
      ...row,
      retained: true,
      isActive: isInactiveMaster(row) || isMissingFromActiveList ? false : row.isActive,
    };
  });
};

const seriesToFormValues = (record = {}) => ({
  seriesKind: record.seriesKind || 'grouped',
  title: record.title || '',
  publicSlug: record.publicSlug || '',
  subtitle: record.subtitle || '',
  summary: record.summary || '',
  organizerId: record.organizerId || '',
  fullParticipationFee: record.fullParticipationFee ?? '',
  primary_image_id: record.primary_image_id || record.primary_image?.id || '',
  image: '',
  isActive: record.isActive !== false,
});

const fieldNoteStyle = {
  margin: 0,
  fontSize: '0.75rem',
  lineHeight: 1.45,
};

const FieldNote = ({ children }) => (
  <p className="yowan-admin-form__field-note" style={fieldNoteStyle}>
    {children}
  </p>
);

const PublicVisibilityPanel = ({ visibility }) => {
  const statusUi = getYowanAdminPublicVisibilityStatusUi(visibility, {
    unknownLabel: '配下セミナー参照',
  });
  const messages = getYowanPublicVisibilityMessages(visibility);
  const diagnostics = getYowanPublicVisibilityDiagnostics(visibility);

  return (
    <section className="yowan-admin-public-visibility">
      <div className="yowan-admin-public-visibility__header">
        <p className="yowan-admin-public-visibility__title">公開面表示判定</p>
        <span className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${statusUi.tone}`}>
          {statusUi.label}
        </span>
      </div>
      {diagnostics.length ? (
        <dl className="yowan-admin-event-detail__definition">
          {diagnostics.map((row) => (
            <div key={row.label}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {messages.length ? (
        <ul className="yowan-admin-public-visibility__list">
          {messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      ) : (
        <p className="yowan-admin-public-visibility__message">
          シリーズが有効な場合、公開条件を満たす配下セミナーが公開面に表示されます。
        </p>
      )}
    </section>
  );
};

const AdminStateNoticePanel = ({ title, issues }) => {
  if (!issues?.length) {
    return null;
  }

  return (
    <section className="yowan-admin-public-visibility yowan-admin-public-visibility--notice">
      <div className="yowan-admin-public-visibility__header">
        <p className="yowan-admin-public-visibility__title">{title}</p>
        <span className="yowan-admin-resource-table__status yowan-admin-resource-table__status--review">
          確認あり
        </span>
      </div>
      <ul className="yowan-admin-public-visibility__list">
        {issues.map((issue) => (
          <li key={issue.code || issue.label}>
            {issue.label}
            {issue.message ? `: ${issue.message}` : ''}
          </li>
        ))}
      </ul>
    </section>
  );
};

const AdminEventSeriesEditPage = () => {
  const content = yowanPageContent['/admin/events/series/:seriesId/edit'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const navigate = useNavigate();
  const { seriesId } = useParams();
  const [record, setRecord] = useState(null);
  const [organizerOptions, setOrganizerOptions] = useState([{ value: '', label: '主催者を選択' }]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {
      seriesKind: 'grouped',
      title: '',
      publicSlug: '',
      subtitle: '',
      summary: '',
      organizerId: '',
      fullParticipationFee: '',
      primary_image_id: '',
      image: '',
      isActive: true,
    },
  });

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const [series, organizers] = await Promise.all([
          fetchYowanAdminSeminarSeriesDetail(seriesId),
          fetchYowanAdminOrganizerList({ pageSize: ADMIN_SERIES_OPTION_PAGE_SIZE, isActive: true }),
        ]);

        if (!isMounted) {
          return;
        }

        setRecord(series);
        const activeOrganizers = organizers.results || [];
        setOrganizerOptions(buildOrganizerOptions(mergeRowsById(
          activeOrganizers,
          markMissingReferencedRowsInactive(series.organizer ? [{ ...series.organizer, retained: true }] : [], activeOrganizers),
        )));
        reset(seriesToFormValues(series));
      } catch (error) {
        if (isMounted) {
          setRecord(null);
          setLoadError('シリーズを読み込めませんでした。');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [reset, seriesId]);

  if (!content) {
    return null;
  }

  const seriesWarningIssues = getYowanSeriesAdminWarningIssues(record || {});

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitMessage('保存しています。');

    try {
      await updateYowanAdminSeminarSeries(seriesId, values);
      navigate(appendYowanPreviewQueryIfNeeded(`/admin/events/series/${seriesId}`, preservedDomainSearch));
    } catch (error) {
      const publicSlugMessage = getYowanApiFieldErrorMessage(error, ['public_slug', 'series_public_slug', 'publicSlug']);

      if (publicSlugMessage) {
        setError('publicSlug', { type: 'server', message: publicSlugMessage });
        setSubmitMessage('公開URL slugを確認してください。');
      } else {
        setSubmitMessage(getYowanAdminMutationErrorMessage(error));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminBlogShell activeNavHref="/admin/events/seminars" routeKey={content.routeKey}>
      <AdminPageHeader
        title={record ? `${record.title} 編集` : content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="detail" to={appendYowanPreviewQueryIfNeeded(`/admin/events/series/${seriesId}`, preservedDomainSearch)}>
            詳細へ戻る
          </Link>,
          <Link key="series" to={appendYowanPreviewQueryIfNeeded('/admin/events/seminars?tab=series', preservedDomainSearch)}>
            シリーズ
          </Link>,
        ]}
      />

      <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
        {isLoading ? <LoadingSpinner label="シリーズを読み込み中" showLabel /> : null}
        {!isLoading && loadError ? <p className="yowan-admin-form__message">{loadError}</p> : null}
        {!isLoading && !loadError ? (
          <>
            <input type="hidden" {...register('seriesKind')} />
            <label className="c-form__field">
              <span className="c-form__label">
                シリーズ名
                <em className="c-form__required">*</em>
              </span>
              <input
                className="c-form__control"
                type="text"
                {...register('title', { required: 'この項目は必須です。' })}
              />
              {errors?.title ? <em className="yowan-admin-form__error">{errors.title.message}</em> : null}
            </label>

            <label className="c-form__field">
              <span className="c-form__label">公開URL slug</span>
              <input
                className="c-form__control"
                type="text"
                readOnly
                aria-readonly="true"
                style={{ cursor: 'not-allowed' }}
                {...register('publicSlug')}
              />
              <FieldNote>
                slugは作成時のみ設定できます。公開URLとSEOの安定性を守るため、編集画面では変更できません。
              </FieldNote>
              {record?.publicPath || record?.canonicalUrl ? (
                <FieldNote>公開URL: {record?.publicPath || '-'} / canonical: {record?.canonicalUrl || '-'}</FieldNote>
              ) : null}
              {errors?.publicSlug ? <em className="yowan-admin-form__error">{errors.publicSlug.message}</em> : null}
            </label>

            <label className="c-form__field">
              <span className="c-form__label">補足タイトル</span>
              <input className="c-form__control" type="text" {...register('subtitle')} />
            </label>

            <label className="c-form__field">
              <span className="c-form__label">シリーズ概要</span>
              <textarea
                className="c-form__control"
                rows={3}
                maxLength={80}
                {...register('summary', {
                  maxLength: {
                    value: 80,
                    message: '80文字以内で入力してください。',
                  },
                })}
              />
              <FieldNote>公開面のセミナー・体験会一覧カードで、シリーズ全体の短い説明として表示します。配下セミナーの概要文とは別責務です。</FieldNote>
              {errors?.summary ? <em className="yowan-admin-form__error">{errors.summary.message}</em> : null}
            </label>

            <div className="c-form__field">
              <span className="c-form__label">主催者</span>
              <select className="c-form__control" {...register('organizerId')}>
                {organizerOptions.map((option) => (
                  <option key={option.value || 'empty'} value={option.value} disabled={option.disabled === true}>
                    {option.label}
                  </option>
                ))}
              </select>
              <FieldNote>シリーズ全体の主催者です。配下セミナーの主催者は各セミナー編集画面で確認してください。</FieldNote>
            </div>

            <label className="c-form__field">
              <span className="c-form__label">シリーズ通し参加料金</span>
              <input className="c-form__control" type="number" min="0" step="100" {...register('fullParticipationFee')} />
              <FieldNote>シリーズ全体を通して参加する場合の案内料金です。セミナー内の通し参加料金とは別概念です。</FieldNote>
            </label>

            <label className="c-form__field">
              <span className="c-form__label">シリーズヘッダー画像</span>
              <input type="hidden" {...register('primary_image_id')} />
              <AdminCurrentImagePreview image={record?.primary_image} />
              <input
                className="c-form__control"
                type="file"
                accept="image/*"
                {...register('image', { validate: validateYowanImageUploadFile })}
              />
              {errors?.image ? <em className="yowan-admin-form__error">{errors.image.message}</em> : null}
              <FieldNote>
                {[
                  '公開シリーズ詳細のヘッダー画像として優先表示します。未設定の場合は配下セミナー画像またはロゴのフォールバックを使います。',
                  YOWAN_IMAGE_UPLOAD_HELP_TEXT,
                ].join(' ')}
              </FieldNote>
            </label>

            <div className="c-form__field yowan-admin-form__toggle-field">
              <span className="c-form__label">有効にする</span>
              <label className="c-form__choice yowan-admin-form__toggle-choice">
                <input type="checkbox" {...register('isActive')} />
                <span>有効にする</span>
              </label>
              <FieldNote>シリーズ自体の有効状態です。公開状態は配下セミナーから導出されるため、この画面では編集しません。</FieldNote>
            </div>
            {record?.isActive === false || (
              record?.publicVisibility && record.publicVisibility.isPubliclyListed !== null
            ) ? (
              <PublicVisibilityPanel
                visibility={record?.isActive === false ? {
                  isPubliclyListed: false,
                  blockers: ['series_inactive'],
                  seriesKind: record.seriesKind || 'grouped',
                  seriesKindLabel: record.seriesKindLabel || 'シリーズ',
                  seriesIsActive: false,
                } : record?.publicVisibility}
              />
            ) : null}
            <AdminStateNoticePanel title="シリーズ内の注意" issues={seriesWarningIssues} />
          </>
        ) : null}

        <div className="yowan-admin-form__actions">
          <button type="submit" className="c-form__submit" disabled={isSubmitting || isLoading || Boolean(loadError)}>
            {isSubmitting ? (
              <>
                <LoadingSpinner size="sm" />
                <span>保存しています。</span>
              </>
            ) : 'シリーズを更新'}
          </button>
        </div>

        {submitMessage ? <p className="yowan-admin-form__message">{submitMessage}</p> : null}
      </form>
    </AdminBlogShell>
  );
};

export default AdminEventSeriesEditPage;
